import { memo, useRef } from 'react';
import { useHorizontalScroll } from 'hooks';
import { useFade } from './hooks/use-fade';
import { useIndicator } from './hooks/use-indicator';
import {
  StyledContainer,
  StyledIndicator,
  StyledTab,
  StyledTabsWrapper,
} from './tabs.styled';
import { Text } from '../text';

type TabsProps<T extends string = string> = {
  tabs: readonly { value: T; text: string }[];
  activeTab: T;
  setTab: (value: T) => void;
};

const TabsComponent = <T extends string = string>({
  tabs,
  activeTab,
  setTab,
}: TabsProps<T>) => {
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  useHorizontalScroll(tabsContainerRef);

  const { fadeLeft, fadeRight } = useFade(tabsContainerRef);
  const { tabRefs, activeIndex, indicatorStyle } = useIndicator(
    tabs,
    activeTab
  );

  const handleTabSelect = (value: T, index: number) => {
    setTab(value);
    tabRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  };

  return (
    <StyledContainer>
      <StyledTabsWrapper
        ref={tabsContainerRef}
        $fadeLeft={fadeLeft}
        $fadeRight={fadeRight}
      >
        {tabs.map((tab, index) => (
          <StyledTab
            key={index}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            data-selected={activeIndex === index}
            onClick={() => handleTabSelect(tab.value, index)}
          >
            <Text variant="button-14" truncate>
              {tab.text}
            </Text>
          </StyledTab>
        ))}

        <StyledIndicator style={indicatorStyle} />
      </StyledTabsWrapper>
    </StyledContainer>
  );
};

export const Tabs = memo(TabsComponent) as typeof TabsComponent;
